import {
  DataProxy,
  gql,
  useMutation,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { dataIdFromObject } from "@outschool/ui-gql";
import { useCallback } from "react";

import {
  AddModeratorToSectionMutation,
  AddModeratorToSectionMutationVariables,
  RemoveModeratorFromSectionMutation,
  RemoveModeratorFromSectionMutationVariables,
  SectionModeratorsFragmentFragment,
  SellerOrgTeachersQuery,
  SellerOrgTeachersQueryVariables,
} from "../../shared/generated/graphql";

export const SellerOrgTeacherFragment = gql`
  fragment SellerOrgTeacherFragment on User {
    uid
    name
    email
    photo
    confirmed_at
    teacher_approved_at
    backgroundChecked
    background_checked_at
    completed_trainings
    hasBeenAssignedToSection
    hasVerifiedIdentity
  }
`;

export const SellerOrgTeachers = gql`
  query SellerOrgTeachers {
    currentUser {
      uid
      sellerOrg {
        uid
        teachers {
          ...SellerOrgTeacherFragment
        }
      }
    }
  }
  ${SellerOrgTeacherFragment}
`;

export function useSellerOrgTeachers(fetchPolicy = "cache-first") {
  const { currentUser } = useSession();
  const { loading, data, error } = useQueryWithPreviousData<
    SellerOrgTeachersQuery,
    SellerOrgTeachersQueryVariables
  >(SellerOrgTeachers, {
    fetchPolicy: fetchPolicy as any,
    skip:
      !!currentUser &&
      !!currentUser.sellerOrg &&
      !currentUser.sellerOrg.currentUserIsOwner,
  });
  const teachers = data?.currentUser?.sellerOrg?.teachers ?? [];
  return { loading, error, teachers };
}

const AddModeratorToSection = gql`
  mutation AddModeratorToSection($sectionUid: String!, $userUid: String!) {
    addSectionModerator(sectionUid: $sectionUid, userUid: $userUid) {
      uid
      name
    }
  }
`;

export function useAddModeratorToSection(sectionUid: string) {
  const [mutate, mutationState] = useMutation<
    AddModeratorToSectionMutation,
    AddModeratorToSectionMutationVariables
  >(AddModeratorToSection);

  const addModerator = useCallback(
    async (userUid: string) => {
      await mutate({
        variables: { userUid, sectionUid },
        update: (cache, { data }) => {
          if (!data) {
            return;
          }
          const newLeader = data.addSectionModerator;
          updateSectionModeratorsCache(cache, sectionUid, mods => [
            ...mods,
            newLeader,
          ]);
        },
      });
    },
    [mutate, sectionUid],
  );

  return [addModerator, mutationState] as const;
}

const RemoveModeratorFromSection = gql`
  mutation RemoveModeratorFromSection($sectionUid: String!, $userUid: String!) {
    removeSectionModerator(sectionUid: $sectionUid, userUid: $userUid)
  }
`;

export function useRemoveModeratorFromSection(
  sectionUid: string,
  userUid: string,
) {
  const [mutate, mutationState] = useMutation<
    RemoveModeratorFromSectionMutation,
    RemoveModeratorFromSectionMutationVariables
  >(RemoveModeratorFromSection);

  const removeModerator = useCallback(async () => {
    await mutate({
      variables: {
        userUid,
        sectionUid,
      },
      update: cache => {
        updateSectionModeratorsCache(cache, sectionUid, mods =>
          mods.filter(mod => mod.uid !== userUid),
        );
      },
    });
  }, [mutate, sectionUid, userUid]);

  return [removeModerator, mutationState] as const;
}

const SectionModeratorsFragment = gql`
  fragment SectionModeratorsFragment on Section {
    uid
    moderators {
      uid
      name
    }
  }
`;

type ModeratorList = SectionModeratorsFragmentFragment["moderators"];
function updateSectionModeratorsCache(
  cache: DataProxy,
  sectionUid: string,
  modifyFn: (cachedModerators: ModeratorList) => ModeratorList,
) {
  {
    const cacheId = dataIdFromObject({
      uid: sectionUid,
      __typename: "Section",
    });
    if (!cacheId) {
      return;
    }
    const data = cache.readFragment<SectionModeratorsFragmentFragment>({
      id: cacheId,
      fragment: SectionModeratorsFragment,
    });

    if (data?.moderators) {
      cache.writeFragment({
        id: cacheId,
        fragment: SectionModeratorsFragment,
        data: {
          ...data,
          moderators: modifyFn(data.moderators),
        },
      });
    }
  }
}
